import { useEffect, useState } from "react";
import { api, brl, API } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, FileText } from "lucide-react";

const FILTERS = [
  { v: "todos", l: "Todos" },
  { v: "aguardando_pagamento", l: "Aguardando pagamento" },
  { v: "pix_informado", l: "Aguardando confirmação" },
  { v: "reservada", l: "Confirmados" },
  { v: "recusada", l: "Não confirmados / Cancelados" },
  { v: "expirada", l: "Expirados" },
];

const BADGE = {
  pix_informado: "bg-amber-100 text-amber-700",
  reservada: "bg-emerald-100 text-emerald-700",
  recusada: "bg-red-100 text-red-700",
  expirada: "bg-slate-200 text-slate-500",
};

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "-");
const fmtD = (s) => (s ? s.split("-").reverse().join("/") : "-");
const toDate = (s) => new Date(s + "T12:00:00");
const cityOf = (a) => (a ? [a.cidade, a.uf].filter(Boolean).join("-") || a.cep : "-");

function Reservas({ user }) {
  const [list, setList] = useState(null);
  const [agenda, setAgenda] = useState({ confirmed: [], pending: [] });
  const [filtro, setFiltro] = useState("todos");
  const [acting, setActing] = useState("");

  const load = () => {
    api.get("/admin/reservations").then((r) => setList(r.data.reservations)).catch(() => setList([]));
    api.get("/agenda/reservadas").then((r) => setAgenda(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const confirmar = async (id) => {
    setActing(id);
    try {
      await api.post(`/admin/reservations/${id}/confirmar-pix`);
      toast.success("Pix confirmado — agendamento confirmado!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao confirmar");
    } finally {
      setActing("");
    }
  };

  const recusar = async (id) => {
    const motivo = window.prompt("Motivo da recusa (opcional):") || "";
    if (!window.confirm("Confirmar a recusa do Pix? O agendamento será cancelado e a data liberada.")) return;
    setActing(id);
    try {
      await api.post(`/admin/reservations/${id}/recusar`, { motivo });
      toast.success("Pagamento não confirmado — data liberada.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao recusar");
    } finally {
      setActing("");
    }
  };

  const filtered = (list || []).filter((r) => filtro === "todos" || r.status === filtro);
  const pendentes = (list || []).filter((r) => r.status === "pix_informado").length;

  return (
    <AdminShell user={user} title="Agenda e Reservas">
      <div className="px-4 mt-4 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="admin-calendar">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold tracking-wider uppercase text-slate-400">Calendário (próximos 3 meses)</p>
            <button onClick={load} className="p-1.5 text-slate-400 hover:text-navy" data-testid="reservas-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="flex justify-center">
            <Calendar
              mode="single"
              showOutsideDays={false}
              fromDate={new Date()}
              toDate={(() => { const d = new Date(); d.setMonth(d.getMonth() + 3); return d; })()}
              modifiers={{
                confirmed: agenda.confirmed.map(toDate),
                pending: agenda.pending.map(toDate),
              }}
              modifiersClassNames={{
                confirmed: "bg-red-500 text-white rounded-md hover:bg-red-500",
                pending: "bg-amber-400 text-navy rounded-md font-bold hover:bg-amber-400",
              }}
            />
          </div>
          <div className="flex justify-center gap-4 mt-2 text-[11px] font-semibold text-slate-500">
            <span data-testid="legend-disponivel">🟢 Disponível</span>
            <span data-testid="legend-pendente">🟡 Aguardando Pix</span>
            <span data-testid="legend-confirmado">🔴 Confirmado</span>
          </div>
        </div>

        {pendentes > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-2xl p-3.5 text-center" data-testid="pending-alert">
            <p className="text-xs font-bold text-amber-700">
              ⚠️ {pendenteLabel(pendentes)} — AGUARDANDO CONFIRMAÇÃO DO PIX
            </p>
          </div>
        )}

        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="reservas-filters">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              data-testid={`reserva-filter-${f.v}`}
              onClick={() => setFiltro(f.v)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition ${
                filtro === f.v ? "bg-navy text-[#F5B301]" : "bg-white text-slate-500 border border-slate-200"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="space-y-3" data-testid="reservas-list">
          {list === null && (
            <div className="flex justify-center py-14">
              <div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {list !== null && filtered.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-14" data-testid="reservas-empty">Nenhuma reserva neste filtro.</p>
          )}
          {filtered.map((r) => {
            const cli = r.quote_snapshot?.cliente || {};
            return (
              <div key={r.reservation_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`reserva-card-${r.reservation_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading font-bold text-navy text-sm">{r.numero_reserva}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${BADGE[r.status] || "bg-slate-100 text-slate-500"}`} data-testid={`reserva-status-${r.reservation_id}`}>
                    {r.status === "pix_informado" ? "RESERVA PENDENTE — AGUARDANDO CONFIRMAÇÃO DO PIX" : r.status_label}
                  </span>
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <p><strong>{cli.nome}</strong> · {cli.telefone}</p>
                  <p>{fmtD(r.data)} às {r.horario} · {r.quote_snapshot?.tipo === "frete" ? "Frete" : "Mudança"}</p>
                  <p>{cityOf(r.quote_snapshot?.origin)} → {cityOf(r.quote_snapshot?.destination)}</p>
                  <p>Total: <strong>{brl(r.payment?.total)}</strong> · Sinal Pix: <strong>{brl(r.payment?.sinal)}</strong></p>
                  <p className="text-slate-400">Cliente informou o pagamento em: {fmtDT(r.pix_declaracao?.informado_em)}</p>
                  {r.confirmacao_empresa && (
                    <p className="text-emerald-600">Confirmado por {r.confirmacao_empresa.admin} em {fmtDT(r.confirmacao_empresa.confirmado_em)}</p>
                  )}
                  {r.recusa && (
                    <p className="text-red-500">Recusado por {r.recusa.admin} em {fmtDT(r.recusa.em)}{r.recusa.motivo ? ` — ${r.recusa.motivo}` : ""}</p>
                  )}
                </div>
                {r.status === "pix_informado" && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button
                      data-testid={`reserva-confirmar-${r.reservation_id}`}
                      onClick={() => confirmar(r.reservation_id)}
                      disabled={acting === r.reservation_id}
                      className="py-2.5 rounded-xl bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> CONFIRMAR RECEBIMENTO DO PIX
                    </button>
                    <button
                      data-testid={`reserva-recusar-${r.reservation_id}`}
                      onClick={() => recusar(r.reservation_id)}
                      disabled={acting === r.reservation_id}
                      className="py-2.5 rounded-xl bg-red-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" /> NÃO CONFIRMAR / RECUSAR
                    </button>
                  </div>
                )}
                <a
                  data-testid={`reserva-pdf-${r.reservation_id}`}
                  href={`${API}/reservations/${r.reservation_id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl border border-slate-200 text-navy text-[11px] font-bold"
                >
                  <FileText className="w-3.5 h-3.5" /> CONTRATO (PDF)
                </a>
                {(r.historico || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-50" data-testid={`reserva-historico-${r.reservation_id}`}>
                    {r.historico.map((h, i) => (
                      <p key={i} className="text-[10px] text-slate-400">
                        {h.acao.replace(/_/g, " ")} · {h.admin} · {fmtDT(h.em)}{h.motivo ? ` · ${h.motivo}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}

function pendenteLabel(n) {
  return n === 1 ? "1 RESERVA PENDENTE" : `${n} RESERVAS PENDENTES`;
}

export default function AdminReservas() {
  return <AdminGate>{(user) => <Reservas user={user} />}</AdminGate>;
}
